import { Fragment, useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { currentLang, t, type MessageKey } from '../i18n/index.ts';
import { Link } from 'react-router';
import { action, idleHeader, request, tokenStore } from '../lib/api.ts';
import { docLink } from '../lib/approvals.ts';
import { day, money } from '../lib/format.ts';
import { useUi } from '../lib/ui-state.tsx';
import { Icon } from './Icon.tsx';
import { Button, IconButton, StatusBadge } from './ui.tsx';

/**
 * ERP PRO AI (Ctrl J / Cmd J): a side panel where the person asks the Finance agent about the
 * business in their own words. The answer is written as it arrives, with the steps the agent
 * takes ("Reading the profit and loss") and the records it read under it. The agent reads only
 * what this person may see and never changes anything itself: a change it prepares is a card the
 * person approves or dismisses. Conversations are private and kept.
 */

interface Step {
  action: string;
  label: string;
  done: boolean;
  ok: boolean;
}
interface ProposalDetails {
  title: string;
  date: string | null;
  rows: { label: string; note?: string; debit?: number; credit?: number; amount?: number }[];
  total: number | null;
}
export interface Proposal {
  id: string;
  action: string;
  summary: string;
  details: ProposalDetails | null;
  state: 'pending' | 'applied' | 'dismissed';
  result?: { id?: string; number?: number } | null;
  created_at?: string;
}
export interface Turn {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  steps?: Step[];
  proposals?: Proposal[];
  created_at?: string;
  error?: string;
  live?: boolean;
}
interface Status {
  ready: boolean;
  agents: { key: string; label: string }[];
  used: number;
  limit: number | null;
}
interface ChatSummary {
  id: string;
  title: string;
  agent?: string;
  last_at: string | null;
}

/** What each agent's empty panel says, and the questions it offers to start with. */
const WELCOME: Record<string, { title: MessageKey; body: MessageKey; placeholder: MessageKey; suggestions: MessageKey[] }> = {
  finance: { title: 'ai.welcome_title', body: 'ai.welcome_body', placeholder: 'ai.placeholder', suggestions: ['ai.suggest_month', 'ai.suggest_owed', 'ai.suggest_unusual', 'ai.suggest_vat', 'ai.suggest_cash'] },
  sales: { title: 'ai.welcome_title_sales', body: 'ai.welcome_body_sales', placeholder: 'ai.placeholder_sales', suggestions: ['ai.suggest_sales_1', 'ai.suggest_sales_2', 'ai.suggest_sales_3', 'ai.suggest_sales_4'] },
  inventory: { title: 'ai.welcome_title_inventory', body: 'ai.welcome_body_inventory', placeholder: 'ai.placeholder_inventory', suggestions: ['ai.suggest_inventory_1', 'ai.suggest_inventory_2', 'ai.suggest_inventory_3', 'ai.suggest_inventory_4'] },
  restaurant: { title: 'ai.welcome_title_restaurant', body: 'ai.welcome_body_restaurant', placeholder: 'ai.placeholder_restaurant', suggestions: ['ai.suggest_restaurant_1', 'ai.suggest_restaurant_2', 'ai.suggest_restaurant_3', 'ai.suggest_restaurant_4'] },
  hr: { title: 'ai.welcome_title_hr', body: 'ai.welcome_body_hr', placeholder: 'ai.placeholder_hr', suggestions: ['ai.suggest_hr_1', 'ai.suggest_hr_2', 'ai.suggest_hr_3', 'ai.suggest_hr_4'] },
};
const welcomeOf = (agent: string) => WELCOME[agent] ?? WELCOME.finance!;

/** The agent that fits the screen the person is on: stock screens get the Inventory agent, and so on. */
const SCREEN_AGENT: [RegExp, string][] = [
  [/^\/manage\/(stock|purchasing|recipes|lots|landed-costs)|^\/barcode/, 'inventory'],
  [/^\/(pos|kitchen|shift|offline|guests|online|lunch)|^\/manage\/(tables|menu|printers)/, 'restaurant'],
  [/^\/(people|team|payroll)|^\/manage\/(team|staff)/, 'hr'],
  [/^\/(crm|receipts|reports|shop|subscriptions|marketing|my-reports|dashboards)/, 'sales'],
  [/^\/(accounting|bank|invoices|insights|budget|assets|consolidation|payments)/, 'finance'],
];
const AGENT_KEY = 'erp.ai.agent';
const screenAgent = (path?: string) => (path ? SCREEN_AGENT.find(([re]) => re.test(path))?.[1] : undefined);
const storedAgent = () => {
  try {
    return localStorage.getItem(AGENT_KEY) ?? undefined;
  } catch {
    return undefined;
  }
};

/** The label of a tool step: a known text, or the action's own name for one the app does not know yet. */
export const stepLabel = (label: string) => {
  const text = t(label as MessageKey);
  return text === label ? label.replace(/^[a-z]+\./, '').replace(/_/g, ' ') : text;
};

/** A question another screen asks for the person ("Ask AI about this"); `n` makes each one new. */
export interface AskAiPrompt {
  text: string;
  n: number;
}

/** Opens the panel and asks `text` in a new conversation, from any screen. */
export function askAi(text: string) {
  window.dispatchEvent(new CustomEvent<string>('erp:ask-ai', { detail: text }));
}

export function AskAi({ page, path, prompt, onClose }: { page?: string; path?: string; prompt?: AskAiPrompt | null; onClose: () => void }) {
  const { fail } = useUi();
  const [status, setStatus] = useState<Status | null>(null);
  const [wanted, setWanted] = useState(() => screenAgent(path) ?? storedAgent() ?? 'finance');
  // The agent asked for when this person has it, else the first they have.
  const agent = status?.agents.length && !status.agents.some((a) => a.key === wanted) ? status.agents[0]!.key : wanted;
  const welcome = welcomeOf(agent);
  const [turns, setTurns] = useState<Turn[]>([]);
  const [chat, setChat] = useState<string | null>(null);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState<ChatSummary[] | null>(null);
  const abort = useRef<AbortController | null>(null);
  const input = useRef<HTMLTextAreaElement>(null);
  const scroller = useRef<HTMLDivElement>(null);
  const panel = useRef<HTMLDivElement>(null);

  const loadStatus = useCallback(() => {
    action<Status>('ai.status', {}).then(setStatus, fail);
  }, [fail]);
  useEffect(loadStatus, [loadStatus]);

  // Focus goes to the question, and back where it was when the panel closes; Escape closes.
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    input.current?.focus({ preventScroll: true });
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && panel.current?.contains(document.activeElement)) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      abort.current?.abort();
      prev?.focus?.();
    };
  }, [onClose]);

  // The newest text stays in view while it is written, unless the person scrolled up to read.
  const pinned = useRef(true);
  useEffect(() => {
    const el = scroller.current;
    if (el && pinned.current) el.scrollTop = el.scrollHeight;
  }, [turns]);

  const patchLast = (fn: (t: Turn) => Turn) => setTurns((all) => all.map((x, i) => (i === all.length - 1 ? fn(x) : x)));

  const ask = async (question: string, newChat = false) => {
    const q = question.trim();
    if (!q || busy) return;
    setText('');
    setBusy(true);
    setHistory(null);
    pinned.current = true;
    setTurns((all) => [...all, { id: `q${all.length}`, role: 'user', text: q }, { id: `a${all.length}`, role: 'assistant', text: '', steps: [], live: true }]);
    const ctrl = new AbortController();
    abort.current = ctrl;
    try {
      const token = tokenStore.get();
      const res = await fetch('/api/ai/ask', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept-Language': currentLang(), ...(token ? { Authorization: `Bearer ${token}`, ...idleHeader() } : {}) },
        body: JSON.stringify({ chat: newChat ? null : chat, agent, text: q, page: page ?? null }),
        signal: ctrl.signal,
      });
      if (!res.ok || !res.body) {
        const body = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
        throw new Error(body?.error?.message ?? t('error.generic'));
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let cut: number;
        while ((cut = buffer.indexOf('\n\n')) >= 0) {
          const block = buffer.slice(0, cut);
          buffer = buffer.slice(cut + 2);
          const event = /^event: (.+)$/m.exec(block)?.[1];
          const data = /^data: (.+)$/m.exec(block)?.[1];
          if (!event || !data) continue;
          const d = JSON.parse(data) as Record<string, any>;
          if (event === 'start') setChat(d.chat as string);
          else if (event === 'text') patchLast((x) => ({ ...x, text: x.text + (d.text as string) }));
          else if (event === 'tool') patchLast((x) => ({ ...x, steps: [...(x.steps ?? []), { action: d.action, label: d.label, done: false, ok: true }] }));
          else if (event === 'tool_done')
            patchLast((x) => {
              const steps = [...(x.steps ?? [])];
              const i = steps.findIndex((s) => s.action === d.action && !s.done);
              if (i >= 0) steps[i] = { ...steps[i]!, done: true, ok: d.ok as boolean };
              return { ...x, steps };
            });
          else if (event === 'proposal')
            patchLast((x) => ({ ...x, proposals: [...(x.proposals ?? []), { id: d.id, action: d.action, summary: d.summary, details: d.details ?? null, state: 'pending' }] }));
          else if (event === 'error') patchLast((x) => ({ ...x, error: d.message as string }));
        }
      }
    } catch (err) {
      if (!ctrl.signal.aborted) patchLast((x) => ({ ...x, error: err instanceof Error && err.message !== 'offline' ? err.message : t('ai.offline') }));
    } finally {
      patchLast((x) => ({ ...x, live: false, ...(ctrl.signal.aborted && !x.text ? { error: t('ai.stopped') } : {}) }));
      abort.current = null;
      setBusy(false);
      loadStatus();
      input.current?.focus({ preventScroll: true });
    }
  };

  const fresh = () => {
    abort.current?.abort();
    setChat(null);
    setTurns([]);
    setHistory(null);
    input.current?.focus();
  };
  /** Another agent starts a new conversation with it; the choice is kept for next time. */
  const switchAgent = (key: string) => {
    if (key === agent) return;
    setWanted(key);
    try {
      localStorage.setItem(AGENT_KEY, key);
    } catch {
      /* not kept: fine */
    }
    fresh();
  };
  // A question from another screen starts its own conversation, once the server says AI is on.
  const asked = useRef(0);
  useEffect(() => {
    if (!prompt || prompt.n === asked.current || !status?.ready) return;
    asked.current = prompt.n;
    if (busy) {
      setText(prompt.text);
      return;
    }
    setChat(null);
    setTurns([]);
    setHistory(null);
    void ask(prompt.text, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prompt, status?.ready]);

  const showHistory = async () => {
    if (history) return setHistory(null);
    try {
      setHistory((await action<{ chats: ChatSummary[] }>('ai.chats', {})).chats);
    } catch (err) {
      fail(err);
    }
  };
  const open = async (id: string) => {
    try {
      const [r, p] = await Promise.all([
        action<{ chat: { id: string; agent: string }; turns: { id: string; role: 'user' | 'assistant'; text: string; created_at: string; tools: { action: string; ok: boolean; label: string }[] | null }[] }>('ai.chat', { chat: id }),
        action<{ proposals: Proposal[] }>('ai.proposals', { chat: id }),
      ]);
      const list: Turn[] = r.turns.map((x) => ({ id: x.id, role: x.role, text: x.text, created_at: x.created_at, steps: (x.tools ?? []).map((s) => ({ action: s.action, label: s.label, done: true, ok: s.ok })) }));
      // A proposal belongs to the answer it was made in: the first answer kept after it.
      for (const prop of p.proposals) {
        const at = list.find((x) => x.role === 'assistant' && (x.created_at ?? '') >= (prop.created_at ?? '')) ?? list.filter((x) => x.role === 'assistant').at(-1);
        if (at) at.proposals = [...(at.proposals ?? []), prop];
      }
      setWanted(r.chat.agent);
      setChat(r.chat.id);
      setTurns(list);
      setHistory(null);
      pinned.current = true;
    } catch (err) {
      fail(err);
    }
  };

  const left = status?.limit != null ? Math.max(0, status.limit - status.used) : null;
  const canAsk = !!status?.ready && left !== 0;

  return (
    <aside className="ai-panel" role="dialog" aria-modal="false" aria-label={t('ai.title')} ref={panel}>
      <header className="ai-head">
        <span className="ai-mark" aria-hidden="true">
          <Icon name="sparkle" size={18} />
        </span>
        <div className="ai-titles">
          {status && status.agents.length > 1 ? (
            <label className="ai-agent-pick">
              <span className="sr-only">{t('ai.choose_agent')}</span>
              <select value={agent} onChange={(e) => switchAgent(e.target.value)} disabled={busy}>
                {status.agents.map((a) => (
                  <option key={a.key} value={a.key}>
                    {t(a.label as MessageKey)}
                  </option>
                ))}
              </select>
              <Icon name="down" size={16} />
            </label>
          ) : (
            <h2>{t((status?.agents.find((a) => a.key === agent)?.label ?? 'ai.agent_finance') as MessageKey)}</h2>
          )}
          <span className="ai-sub">
            <Icon name="shieldCheck" size={13} /> {t('ai.reads_only')}
          </span>
        </div>
        <IconButton icon="history" label={t('ai.history')} onClick={() => void showHistory()} className={history ? 'active' : ''} />
        <IconButton icon="plus" label={t('ai.new')} onClick={fresh} />
        <IconButton icon="x" label={t('common.close')} onClick={onClose} />
      </header>

      <div
        className="ai-body"
        ref={scroller}
        onScroll={(e) => {
          const el = e.currentTarget;
          pinned.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
        }}
        aria-live="polite"
        aria-busy={busy}
      >
        {history ? (
          <div className="ai-history">
            <h3>{t('ai.history')}</h3>
            {history.length ? (
              history.map((c) => (
                <button key={c.id} type="button" className="ai-history-item" onClick={() => void open(c.id)}>
                  <Icon name="chat" size={16} />
                  <span className="ai-history-title">
                    {c.title}
                    {c.agent && c.agent !== 'finance' ? <span className="ai-history-agent">{t(`ai.agent_${c.agent}` as MessageKey)}</span> : null}
                  </span>
                  {c.last_at ? <span className="ai-history-when">{new Date(c.last_at).toLocaleDateString()}</span> : null}
                </button>
              ))
            ) : (
              <p className="muted">{t('ai.history_empty')}</p>
            )}
          </div>
        ) : status && !status.ready ? (
          <div className="ai-welcome">
            <span className="ai-hero" aria-hidden="true">
              <Icon name="sparkle" size={28} />
            </span>
            <h3>{t('ai.not_ready_title')}</h3>
            <p className="muted">{t('ai.not_ready_body')}</p>
          </div>
        ) : turns.length ? (
          turns.map((x) => (x.role === 'user' ? <div key={x.id} className="ai-q">{x.text}</div> : <Answer key={x.id} turn={x} />))
        ) : (
          <div className="ai-welcome">
            <span className="ai-hero" aria-hidden="true">
              <Icon name="sparkle" size={28} />
            </span>
            <h3>{t(welcome.title)}</h3>
            <p className="muted">{t(welcome.body)}</p>
            <div className="ai-suggestions">
              {welcome.suggestions.map((k) => (
                <button key={k} type="button" className="ai-chip" disabled={!canAsk} onClick={() => void ask(t(k))}>
                  {t(k)}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <form
        className="ai-compose"
        onSubmit={(e) => {
          e.preventDefault();
          void ask(text);
        }}
      >
        <textarea
          ref={input}
          value={text}
          rows={1}
          maxLength={4000}
          placeholder={left === 0 ? t('ai.limit_reached') : t(welcome.placeholder)}
          aria-label={t(welcome.placeholder)}
          disabled={!canAsk}
          onChange={(e) => {
            setText(e.target.value);
            e.currentTarget.style.height = 'auto';
            e.currentTarget.style.height = `${Math.min(e.currentTarget.scrollHeight, 160)}px`;
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              void ask(text);
            }
          }}
        />
        {busy ? (
          <Button variant="secondary" icon="pause" onClick={() => abort.current?.abort()}>
            {t('ai.stop')}
          </Button>
        ) : (
          <IconButton icon="send" variant="primary" label={t('ai.send')} type="submit" disabled={!canAsk || !text.trim()} className="ai-send" />
        )}
      </form>
      <p className="ai-foot">
        {t('ai.disclaimer')}
        {left !== null ? <span> · {t('ai.left', { n: left, limit: status!.limit! })}</span> : null}
      </p>
    </aside>
  );
}

export function Answer({ turn, readOnly = false }: { turn: Turn; readOnly?: boolean }) {
  const steps = turn.steps ?? [];
  const working = turn.live && !turn.text;
  return (
    <div className="ai-a">
      {steps.length ? (
        <ul className="ai-steps" aria-label={t('ai.steps')}>
          {steps.map((s, i) => (
            <li key={`${s.action}${i}`} className={s.done ? (s.ok ? 'ok' : 'bad') : 'run'}>
              <Icon name={s.done ? (s.ok ? 'check' : 'alert') : 'circle'} size={14} />
              <span>{stepLabel(s.label)}</span>
            </li>
          ))}
        </ul>
      ) : null}
      {working && !steps.length ? (
        <p className="ai-thinking">
          <span className="ai-dots" aria-hidden="true">
            <i />
            <i />
            <i />
          </span>
          {t('ai.thinking')}
        </p>
      ) : null}
      {turn.text ? <div className="ai-md">{markdown(turn.text)}</div> : null}
      {turn.proposals?.length ? (
        <div className="ai-proposals">
          {turn.proposals.map((p) => (
            <ProposalCard key={p.id} proposal={p} readOnly={readOnly} />
          ))}
        </div>
      ) : null}
      {turn.error ? (
        <p className="ai-error" role="alert">
          <Icon name="alert" size={14} /> {turn.error}
        </p>
      ) : null}
    </div>
  );
}

const KIND: Record<string, { icon: 'book' | 'invoice' | 'bank' | 'bag'; label: MessageKey }> = {
  'purchase.save_order': { icon: 'bag', label: 'ai.p_order' },
  'accounting.post': { icon: 'book', label: 'ai.p_entry' },
  'invoicing.save_draft': { icon: 'invoice', label: 'ai.p_invoice' },
  'bank.match': { icon: 'bank', label: 'ai.p_match' },
};

/** Where a done proposal's result opens. */
function resultLink(p: Proposal): string | null {
  if (p.action === 'invoicing.save_draft' && p.result?.id) return docLink('invoicing.invoice', p.result.id);
  if (p.action === 'accounting.post') return '/accounting';
  if (p.action === 'bank.match') return '/bank';
  if (p.action === 'purchase.save_order') return '/manage/purchasing';
  return null;
}

/** A change the agent prepared: shown in full, and done only when the person approves it. */
/** `readOnly`: the owner looking back at someone's conversation; nothing can be decided from it. */
export function ProposalCard({ proposal, readOnly = false }: { proposal: Proposal; readOnly?: boolean }) {
  const { fail } = useUi();
  const [p, setP] = useState(proposal);
  const [busy, setBusy] = useState<'apply' | 'dismiss' | null>(null);
  useEffect(() => setP(proposal), [proposal]);
  const kind = KIND[p.action] ?? { icon: 'sparkle' as const, label: 'ai.p_change' as MessageKey };
  const d = p.details;
  const decide = async (what: 'apply' | 'dismiss') => {
    setBusy(what);
    try {
      const done = what === 'apply' ? await request<Proposal>('POST', '/api/ai/apply', { proposal: p.id }) : await action<Proposal>('ai.dismiss', { proposal: p.id });
      setP((x) => ({ ...x, ...done }));
    } catch (err) {
      fail(err);
    } finally {
      setBusy(null);
    }
  };
  const ledger = d?.rows.some((r) => r.debit !== undefined || r.credit !== undefined);
  const link = p.state === 'applied' ? resultLink(p) : null;
  return (
    <section className={`ai-card state-${p.state}`} aria-label={t(kind.label)}>
      <header className="ai-card-head">
        <span className="ai-card-icon" aria-hidden="true">
          <Icon name={kind.icon} size={16} />
        </span>
        <span className="ai-card-kind">{t(kind.label)}</span>
        {p.state === 'pending' ? (
          <StatusBadge tone="warn" icon="clock" label={t('ai.p_waiting')} />
        ) : p.state === 'applied' ? (
          <StatusBadge tone="ok" icon="checkCircle" label={t('ai.p_applied')} />
        ) : (
          <StatusBadge tone="neutral" icon="x" label={t('ai.p_dismissed')} />
        )}
      </header>
      <p className="ai-card-summary">{p.summary}</p>
      {d ? (
        <div className="ai-card-body">
          <p className="ai-card-title">
            <strong>{d.title}</strong>
            {d.date ? <span className="muted"> · {day(d.date)}</span> : null}
          </p>
          {d.rows.length ? (
            <table className="ai-card-rows">
              {ledger ? (
                <thead>
                  <tr>
                    <th scope="col">{t('ai.p_account')}</th>
                    <th scope="col">{t('ai.p_debit')}</th>
                    <th scope="col">{t('ai.p_credit')}</th>
                  </tr>
                </thead>
              ) : null}
              <tbody>
                {d.rows.map((r, i) => (
                  <tr key={i}>
                    <td>
                      <span className="ai-card-label">{r.label}</span>
                      {r.note ? <span className="ai-card-note">{r.note}</span> : null}
                    </td>
                    {ledger ? (
                      <>
                        <td className="num">{r.debit ? money(r.debit) : ''}</td>
                        <td className="num">{r.credit ? money(r.credit) : ''}</td>
                      </>
                    ) : (
                      <td className="num">{r.amount !== undefined ? money(r.amount) : ''}</td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
          {d.total !== null ? (
            <p className="ai-card-total">
              <span>{p.action === 'bank.match' ? t('ai.p_line_amount') : p.action === 'accounting.post' ? t('ai.p_entry_total') : p.action === 'purchase.save_order' ? t('ai.p_order_total') : t('ai.p_invoice_total')}</span>
              <strong>{money(d.total)}</strong>
            </p>
          ) : null}
        </div>
      ) : null}
      {p.state === 'pending' && !readOnly ? (
        <div className="ai-card-actions">
          <Button variant="primary" size="sm" icon="check" busy={busy === 'apply'} disabled={!!busy} onClick={() => void decide('apply')}>
            {p.action === 'invoicing.save_draft' ? t('ai.p_create_draft') : p.action === 'purchase.save_order' ? t('ai.p_create_order') : p.action === 'bank.match' ? t('ai.p_match_it') : t('ai.p_post')}
          </Button>
          <Button variant="ghost" size="sm" busy={busy === 'dismiss'} disabled={!!busy} onClick={() => void decide('dismiss')}>
            {t('ai.p_dismiss')}
          </Button>
        </div>
      ) : link ? (
        <p className="ai-card-done">
          <Link to={link}>
            {t('ai.p_open')} <Icon name="chevronRight" size={14} />
          </Link>
        </p>
      ) : null}
      {p.state === 'pending' && !readOnly ? <p className="ai-card-hint">{t('ai.p_hint')}</p> : null}
    </section>
  );
}

/**
 * The small Markdown the agent writes (paragraphs, lists, tables, ### headings, bold, italics,
 * code), drawn as React elements: no HTML from the answer ever reaches the page.
 */
export function markdown(src: string): ReactNode[] {
  const lines = src.replace(/\r/g, '').split('\n');
  const out: ReactNode[] = [];
  let i = 0;
  const isRow = (l: string | undefined) => !!l && /^\s*\|.*\|\s*$/.test(l);
  const cells = (l: string) =>
    l
      .trim()
      .replace(/^\||\|$/g, '')
      .split('|')
      .map((c) => c.trim());
  while (i < lines.length) {
    const line = lines[i]!;
    if (!line.trim()) {
      i++;
      continue;
    }
    const h = /^(#{1,6})\s+(.*)$/.exec(line);
    if (h) {
      out.push(<h4 key={i}>{inline(h[2]!)}</h4>);
      i++;
      continue;
    }
    if (isRow(line) && /^\s*\|?\s*:?-{2,}/.test(lines[i + 1] ?? '')) {
      const head = cells(line);
      i += 2;
      const rows: string[][] = [];
      while (isRow(lines[i])) rows.push(cells(lines[i++]!));
      out.push(
        <div key={i} className="ai-table">
          <table>
            <thead>
              <tr>
                {head.map((c, j) => (
                  <th key={j}>{inline(c)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, k) => (
                <tr key={k}>
                  {r.map((c, j) => (
                    <td key={j} className={/^[-+(]?[\d.,\s]+%?\)?(\s?[A-Z]{3})?$/.test(c) ? 'num' : undefined}>
                      {inline(c)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      continue;
    }
    const bullet = /^\s*[-*•]\s+/;
    const numbered = /^\s*\d+[.)]\s+/;
    if (bullet.test(line) || numbered.test(line)) {
      const ordered = numbered.test(line);
      const re = ordered ? numbered : bullet;
      const items: string[] = [];
      while (i < lines.length && re.test(lines[i]!)) items.push(lines[i++]!.replace(re, ''));
      const List = ordered ? 'ol' : 'ul';
      out.push(
        <List key={i}>
          {items.map((it, j) => (
            <li key={j}>{inline(it)}</li>
          ))}
        </List>,
      );
      continue;
    }
    const para: string[] = [];
    while (i < lines.length && lines[i]!.trim() && !/^(#{1,6})\s/.test(lines[i]!) && !bullet.test(lines[i]!) && !numbered.test(lines[i]!) && !isRow(lines[i])) para.push(lines[i++]!);
    if (!para.length) para.push(lines[i++]!);
    out.push(
      <p key={i}>
        {para.map((p, j) => (
          <Fragment key={j}>
            {j ? <br /> : null}
            {inline(p)}
          </Fragment>
        ))}
      </p>,
    );
  }
  return out;
}

function inline(s: string): ReactNode[] {
  const out: ReactNode[] = [];
  const re = /(\*\*[^*]+\*\*|`[^`]+`|\*[^*\s][^*]*\*|_[^_\s][^_]*_)/g;
  let last = 0;
  for (const m of s.matchAll(re)) {
    if (m.index > last) out.push(s.slice(last, m.index));
    const tok = m[0];
    const k = `${m.index}`;
    if (tok.startsWith('**')) out.push(<strong key={k}>{tok.slice(2, -2)}</strong>);
    else if (tok.startsWith('`')) out.push(<code key={k}>{tok.slice(1, -1)}</code>);
    else out.push(<em key={k}>{tok.slice(1, -1)}</em>);
    last = m.index + tok.length;
  }
  if (last < s.length) out.push(s.slice(last));
  return out;
}
